import React, { useState } from 'react';
import {
  Box, Button, Chip, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';

const ToolSelectorStep = ({ config, response = {}, onChange, disabled }) => {
  const theme = useTheme();
  const selected = response.tools || [];
  const [filter, setFilter] = useState('');
  const [customTool, setCustomTool] = useState('');

  const visibleTools = config.tools.filter((t) =>
    !filter || t.toLowerCase().includes(filter.toLowerCase()),
  );

  const toggle = (tool) => {
    const next = selected.includes(tool)
      ? selected.filter((t) => t !== tool)
      : [...selected, tool];
    onChange({ ...response, tools: next });
  };

  const addCustomTool = () => {
    const tool = customTool.trim();
    if (!tool || selected.includes(tool)) return;
    onChange({ ...response, tools: [...selected, tool] });
    setCustomTool('');
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="body2" fontWeight={600} mb={1}>
          {config.prompt}
        </Typography>

        {config.tools.length > 12 && (
          <TextField
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter tools…"
            size="small"
            sx={{ mb: 1.5, maxWidth: 280 }}
            disabled={disabled}
          />
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {visibleTools.map((tool) => {
            const isSelected = selected.includes(tool);
            return (
              <Chip
                key={tool}
                label={tool}
                size="small"
                clickable={!disabled}
                onClick={() => !disabled && toggle(tool)}
                variant={isSelected ? 'filled' : 'outlined'}
                color={isSelected ? 'primary' : 'default'}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.78rem',
                  ...(isSelected && {
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                  }),
                }}
              />
            );
          })}
        </Box>

        {selected.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Selected: {selected.join(', ')}
          </Typography>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            value={customTool}
            onChange={(e) => setCustomTool(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomTool();
              }
            }}
            placeholder="Add your own tool or concept..."
            size="small"
            disabled={disabled}
            sx={{ maxWidth: { sm: 360 } }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={addCustomTool}
            disabled={disabled || !customTool.trim()}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            Add
          </Button>
        </Stack>
      </Box>

      <Box>
        <Typography variant="body2" fontWeight={600} mb={0.75}>
          Why these tools? What will each one do?
        </Typography>
        <TextField
          value={response.reasoning || ''}
          onChange={(e) => onChange({ ...response, reasoning: e.target.value })}
          placeholder="Explain your choices — what task does each tool/concept handle?"
          multiline
          minRows={3}
          fullWidth
          disabled={disabled}
          variant="outlined"
          size="small"
        />
      </Box>
    </Stack>
  );
};

export default ToolSelectorStep;
